/**
 * BatchSimulationInfo - InfoButton body for the batch simulation tool
 */
import type { ReactElement } from 'react';

import { InfoList, InfoSection, InfoTip } from '@/components/InfoButton/InfoButton';

export default function BatchSimulationInfo(): ReactElement {
  return (
    <>
      <InfoSection title="What is this?">
        <p>
          Runs 10,000 opening hands at once to measure your deck&apos;s consistency. Every hand is judged by the same engine as the sample
          hand tool, using the shared simulation settings at the top of the panel.
        </p>
      </InfoSection>

      <InfoSection title="Why the ± figure matters">
        <p>
          A batch is a random sample, so its keep % is an estimate. The ± is the 95% margin of error: at 10,000 hands it is about one point,
          which is small enough that a re-run after a real change moves the number more than chance does.
        </p>
        <InfoList
          items={[
            'The sample size is fixed because the run takes well under a second — a smaller one would only widen the error bar',
            'A re-run whose change falls inside the margin is reported as “no real change” rather than a misleading ±1',
          ]}
        />
      </InfoSection>

      <InfoSection title="What counts as keepable">
        <InfoList
          items={[
            'Keepable = a clean "Keep" — enough sources, not flooded, every spell castable, and a play on time',
            'Everything else is attributed to a single dominant failure, so the breakdown always adds up to 100%',
            'Priority when a hand fails in several ways: screw → flood → all lands → color → slow',
            'Adventures, split cards and modal DFCs count as castable from any of their faces',
          ]}
        />
      </InfoSection>

      <InfoSection title="Failure modes">
        <InfoList
          items={[
            'Screw — below the minimum effective mana sources',
            'Flood — more raw lands than the flood ceiling',
            'Color — spells needing a color the hand can\u2019t make (cost is never counted as color screw)',
            'Slow — first castable spell arrives later than your target turn',
            'All lands — no spells at all',
          ]}
        />
      </InfoSection>

      <InfoSection title="First play & stalls">
        <p>
          Every hand is also goldfished for five turns — draw, land drop, cast — to find the turn it first resolves a spell. The
          &ldquo;none&rdquo; bucket is hands that never cast anything inside that window.
        </p>
        <InfoList
          items={[
            'Unlike the sample hand’s “first play”, this one does count draws, so it is the more realistic number',
            'Stalled = the run ended on two or more turns with no land drop and nothing castable',
            'The turn order (play/draw) comes from the shared simulation settings',
            'No ramp, tutors, card draw, scry or cost reduction is modelled — it is a mana-and-curve model only',
          ]}
        />
      </InfoSection>

      <InfoSection title="Mana rock scoring">
        <p>Mana rocks contribute fractional land equivalents. A rock you don&apos;t have the lands to cast counts as 0.</p>
        <InfoList
          items={[
            'Sol Ring (cost 1, produces 2) with 1 land → +0.76',
            '2-cost rock (produces 1) with 2 lands → +0.33',
            '3-cost rock (produces 1) with 3 lands → +0.25',
            'Rocks lift you over the source floor but never count toward flood',
          ]}
        />
      </InfoSection>

      <InfoTip>
        Change one card, re-run, and read the delta. That run-over-run comparison is the honest way to tell whether a mana base change
        helped — a single redrawn hand never is.
      </InfoTip>
    </>
  );
}
